import { inject, Injectable } from '@angular/core';
import { RestService, SettingsService } from '@infodata-group/inf-commons';
import { combineLatest, map, ReplaySubject } from 'rxjs';
import { WidgetFactory } from '../engine/factory/widget-factory';
import { WidgetSettingType } from '../engine/types/widget-setting.type';
import { Widget } from '../engine/widget/widget';

const DASHBOARD_KEY = 'dashboard';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  protected readonly settingsService = inject(SettingsService);
  protected readonly restService: RestService = inject(RestService);

  protected allWidgets: Widget[] = [];
  protected allWidgets$$ = new ReplaySubject<Widget[]>();

  protected settingsWidgets: WidgetSettingType[] = [];
  protected settingsWidgets$$ = new ReplaySubject<WidgetSettingType[]>();

  private widgets$ = combineLatest([
    this.allWidgets$$,
    this.settingsWidgets$$,
  ]).pipe(
    map(([widgets, settingsWidgets]) => {
      settingsWidgets.forEach((setting) => {
        const widget = widgets.find((w) => w.id === setting.id);
        if (widget) {
          widget.applySettings(setting);
        }
      });
      return widgets;
    }),
  );

  public displayedWidgets$ = this.widgets$.pipe(
    map((widgets) => widgets.filter((widget) => widget.visible)),
  );
  public hiddenWidgets$ = this.widgets$.pipe(
    map((widgets) => widgets.filter((widget) => !widget.visible)),
  );
  
  public init() {
    this.settingsWidgets = this.settingsService.getSetting(DASHBOARD_KEY) || [];
    this.settingsWidgets$$.next(this.settingsWidgets);
  }

  public loadWidgets(): void {
    this.restService.getAll(new WidgetFactory()).subscribe({
      next: (widgets) => {
        this.allWidgets = widgets;
        this.allWidgets$$.next(widgets);
        this.checkSettingsAfterLoad();
      },
    });
  }

  protected checkSettingsAfterLoad(): void {
    this.allWidgets.forEach((widget) => {
      if (this.settingsWidgets.find((setting) => setting.id === widget.id)) {
        return;
      }
      const setting = widget.serialize();
      setting.visible = false;
      this.settingsWidgets.push(setting);
    });
    this.settingsWidgets$$.next(this.settingsWidgets);
  }

  public toggleVisibilityWidget(widget: Widget, visibility: boolean): void {
    const setting = this.getWidgetSetting(widget.id);
    setting.visible = visibility;
    if (!visibility) {
      delete setting.x;
      delete setting.y;
    }
    this.saveDashboard();
    this.settingsWidgets$$.next(this.settingsWidgets);
  }

  public updateSize(widgetId: string, width: number, height: number): void {
    const setting = this.getWidgetSetting(widgetId);
    setting.width = width;
    setting.height = height;
    this.saveDashboard();
  }

  public updatePosition(widgetId: string, x: number, y: number): void {
    const setting = this.getWidgetSetting(widgetId);
    setting.x = x;
    setting.y = y;
    this.saveDashboard();
  }

  protected saveDashboard(): void {
    this.settingsService.addSetting(DASHBOARD_KEY, this.settingsWidgets);
  }

  protected getWidgetSetting(widgetId: string): WidgetSettingType {
    const setting = this.settingsWidgets.find(
      (setting) => setting.id === widgetId,
    );
    if (setting) {
      return setting;
    }
    throw Error('Widget settings not found');
  }

  public clear() {
    this.allWidgets = [];
    this.allWidgets$$.next([]);
    this.settingsWidgets = [];
    this.settingsWidgets$$.next([]);
  }
}
